import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentMimeKind } from '../types';
import { detectImageOrientationTurns } from '../ocr/tesseract-ocr-provider';
import { filenameWithRotation, rotateImageUrlToBlob } from './rotate-image';

export function useImageSession(args: {
    url: string;
    mimeKind: DocumentMimeKind;
    loading: boolean;
    allowRotate?: boolean;
    allowOcr?: boolean;
    autoRunOcr?: boolean;
    fileName?: string;
    onOrientedSave?: (blob: Blob, fileName: string) => void | Promise<void>;
    containerRef: React.RefObject<HTMLDivElement>;
    scale: number;
    setScale: (n: number) => void;
    panRef: React.MutableRefObject<{ x: number; y: number }>;
    setPan: (p: { x: number; y: number }) => void;
    lastFitRef: React.MutableRefObject<number>;
    setFitScale: (n: number) => void;
    onStatus: (s: string) => void;
    onAlignFinished: (shouldOcr: boolean) => void;
}) {
    const imageRef = useRef<HTMLImageElement>(null);
    const orientedUrlRef = useRef<string | null>(null);
    const lastAlignedUrlRef = useRef('');
    const alignSeqRef = useRef(0);
    const [displayUrl, setDisplayUrl] = useState(args.url);
    const [aligning, setAligning] = useState(false);
    const [imageNatural, setImageNatural] = useState({ w: 0, h: 0 });
    const [rotateTurns, setRotateTurns] = useState(0);
    const [rotateSaving, setRotateSaving] = useState(false);
    const [orientationDirty, setOrientationDirty] = useState(false);

    const revokeOrientedUrl = useCallback(() => {
        if (orientedUrlRef.current) {
            try { URL.revokeObjectURL(orientedUrlRef.current); } catch { }
            orientedUrlRef.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        revokeOrientedUrl();
        setDisplayUrl(args.url || '');
        lastAlignedUrlRef.current = '';
        setRotateTurns(0);
        setRotateSaving(false);
        setOrientationDirty(false);
        setAligning(false);
        setImageNatural({ w: 0, h: 0 });
    }, [args.url, revokeOrientedUrl]);

    const fitImageToStage = useCallback(() => {
        const container = args.containerRef.current;
        const img = imageRef.current;
        if (!container || !img || !img.naturalWidth || !img.naturalHeight) {
            return;
        }
        const availableW = Math.max(40, container.clientWidth - 16);
        const availableH = Math.max(40, container.clientHeight - 16);
        const turns = rotateTurns;
        const srcW = turns % 2 === 1 ? img.naturalHeight : img.naturalWidth;
        const srcH = turns % 2 === 1 ? img.naturalWidth : img.naturalHeight;
        const fitted = Math.min(availableW / srcW, availableH / srcH);
        const nextFit = fitted > 0 ? fitted : 1;
        if (Math.abs(nextFit - args.lastFitRef.current) > 0.002) {
            args.lastFitRef.current = nextFit;
            args.setFitScale(nextFit);
        }
    }, [args, rotateTurns]);

    const rotateBy = (delta: number) => {
        if (!args.allowRotate) {
            return;
        }
        setRotateTurns(t => (((t + delta) % 4) + 4) % 4);
        setOrientationDirty(true);
        args.setPan({ x: 0, y: 0 });
        args.panRef.current = { x: 0, y: 0 };
    };

    const saveOrientation = async () => {
        if (!args.allowRotate || !args.onOrientedSave || rotateSaving) {
            return;
        }
        if (args.mimeKind !== 'image') {
            args.onStatus('Save orientation supports images');
            return;
        }
        if (!rotateTurns && !orientationDirty) {
            return;
        }
        setRotateSaving(true);
        try {
            const source = displayUrl || args.url;
            const blob = rotateTurns
                ? await rotateImageUrlToBlob(source, rotateTurns)
                : await (await fetch(source)).blob();
            const base = args.fileName || 'document.jpg';
            const name = filenameWithRotation(base, rotateTurns || 0);
            await Promise.resolve(args.onOrientedSave(blob, name));
            revokeOrientedUrl();
            const nextUrl = URL.createObjectURL(blob);
            orientedUrlRef.current = nextUrl;
            setDisplayUrl(nextUrl);
            setRotateTurns(0);
            setOrientationDirty(false);
            args.lastFitRef.current = 0;
            args.onStatus('Orientation saved');
        } catch {
            args.onStatus('Save orientation failed');
        } finally {
            setRotateSaving(false);
        }
    };

    useEffect(() => {
        return () => {
            revokeOrientedUrl();
        };
    }, [revokeOrientedUrl]);

    useEffect(() => {
        if (args.loading || args.mimeKind !== 'image' || !args.url) {
            return;
        }
        if (lastAlignedUrlRef.current === args.url) {
            return;
        }
        const targetUrl = args.url;
        const seq = ++alignSeqRef.current;
        let cancelled = false;

        const run = async () => {
            setAligning(true);
            args.onStatus('Aligning for best fit…');
            let alignedTurns = 0;
            try {
                const turns = args.allowRotate
                    ? await detectImageOrientationTurns(targetUrl)
                    : 0;
                if (cancelled || seq !== alignSeqRef.current) {
                    return;
                }
                alignedTurns = turns;
                if (turns > 0) {
                    const blob = await rotateImageUrlToBlob(targetUrl, turns);
                    if (cancelled || seq !== alignSeqRef.current) {
                        return;
                    }
                    revokeOrientedUrl();
                    const nextUrl = URL.createObjectURL(blob);
                    orientedUrlRef.current = nextUrl;
                    setDisplayUrl(nextUrl);
                    setRotateTurns(0);
                    setOrientationDirty(true);
                } else {
                    setDisplayUrl(targetUrl);
                    setRotateTurns(0);
                    setOrientationDirty(false);
                }
                args.setScale(1);
                args.panRef.current = { x: 0, y: 0 };
                args.setPan({ x: 0, y: 0 });
                args.lastFitRef.current = 0;
                args.onStatus(alignedTurns > 0
                    ? (args.onOrientedSave
                        ? `Aligned for viewing · save to keep`
                        : `Aligned for viewing · not saved`)
                    : 'Fit');
            } catch {
                if (!cancelled && seq === alignSeqRef.current) {
                    setDisplayUrl(targetUrl);
                    setOrientationDirty(false);
                    args.onStatus('Fit');
                }
            } finally {
                if (!cancelled && seq === alignSeqRef.current) {
                    lastAlignedUrlRef.current = targetUrl;
                    setAligning(false);
                    window.setTimeout(() => fitImageToStage(), 0);
                    args.onAlignFinished(!!(args.allowOcr && args.autoRunOcr !== false));
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [args.loading, args.mimeKind, args.url, args.allowRotate, args.allowOcr, args.autoRunOcr]);

    useEffect(() => {
        if (args.loading || args.mimeKind !== 'image') {
            return;
        }
        const id = window.setTimeout(() => fitImageToStage(), 0);
        return () => window.clearTimeout(id);
    }, [args.loading, args.mimeKind, displayUrl, rotateTurns, args.scale, fitImageToStage]);

    useEffect(() => {
        if (args.mimeKind !== 'image' || !args.containerRef.current) {
            return;
        }
        const ro = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => fitImageToStage())
            : null;
        if (ro) {
            ro.observe(args.containerRef.current);
        }
        return () => {
            if (ro) {
                ro.disconnect();
            }
        };
    }, [args.mimeKind, fitImageToStage, args.containerRef]);

    return {
        displayUrl,
        aligning,
        imageNatural,
        setImageNatural,
        imageRef,
        rotateTurns,
        rotateSaving,
        orientationDirty,
        rotateBy,
        saveOrientation,
        fitImageToStage,
        reset
    };
}
